<template>
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
            <!-- Backdrop with blur -->
            <div
                class="absolute inset-0 bg-slate-800/50 backdrop-blur-sm"
                @click="$emit('close')"
            ></div>

            <!-- Modal -->
            <div
                class="relative rounded-2xl shadow-xl w-full max-w-md z-10 overflow-hidden dark:bg-slate-800"
                :style="modalBgStyle"
            >
                <!-- Close button -->
                <button
                    class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    @click="$emit('close')"
                >
                    <svg
                        class="w-5 h-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </button>

                <div
                    class="px-8 pt-8 pb-7 flex flex-col items-center text-center"
                >
                    <!-- Logo -->
                    <p
                        class="flex font-bold text-xl leading-tight tracking-tight mb-4"
                    >
                        <span class="text-orange">10x</span>
                        <span class="text-slate-800 dark:text-slate-100">TRAVEL&plus;</span>
                    </p>

                    <!-- Heading -->
                    <h2 class="text-2xl font-bold text-slate-800 dark:text-slate-100 leading-snug">
                        {{ title }}
                    </h2>

                    <!-- Subtext -->
                    <p
                        class="text-slate-500 dark:text-slate-400 text-sm mt-2 max-w-xs leading-relaxed"
                    >
                        {{ description }}
                    </p>

                    <!-- Billing toggle -->
                    <div
                        class="flex items-center gap-1 mt-5 bg-slate-100 dark:bg-slate-700 rounded-lg p-1"
                    >
                        <button
                            class="px-4 py-1.5 text-sm font-medium rounded-md transition-all"
                            :class="
                                billingCycle === 'monthly'
                                    ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            "
                            @click="billingCycle = 'monthly'"
                        >
                            Monthly
                        </button>
                        <button
                            class="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-all"
                            :class="
                                billingCycle === 'yearly'
                                    ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            "
                            @click="billingCycle = 'yearly'"
                        >
                            Yearly
                            <span
                                class="text-xs font-semibold text-orange bg-orange/10 px-1.5 py-0.5 rounded"
                            >
                                2 Months Free
                            </span>
                        </button>
                    </div>

                    <!-- Feature list -->
                    <ul class="mt-5 w-full flex flex-col gap-3 text-left">
                        <li
                            v-for="feature in visibleFeatures"
                            :key="feature"
                            class="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200"
                        >
                            <span
                                class="shrink-0 w-5 h-5 rounded-full bg-orange/10 flex items-center justify-center"
                            >
                                <svg
                                    class="w-3 h-3 text-orange"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </span>
                            {{ feature }}
                        </li>
                    </ul>

                    <!-- CTA button -->
                    <button
                        class="mt-6 w-full bg-orange hover:bg-orange-dark text-white text-sm font-semibold py-3 px-4 rounded-lg shadow-sm transition-colors"
                        @click="handleUpgrade"
                    >
                        {{ ctaLabel }}
                    </button>

                    <!-- Learn more link -->
                    <a
                        href="/10xtravel-plus"
                        class="mt-3 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                    >
                        Learn more
                    </a>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router } from '@inertiajs/vue3';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useThemeStore } from '@/store/theme';

const { transferBonusAlertsEnabled } = useFeatureFlags();

const themeStore = useThemeStore();

const modalBgStyle = computed(() =>
    themeStore.isDark
        ? 'background: linear-gradient(to bottom, rgba(253, 66, 29, 0.08) 0%, #1e293b 40%);'
        : 'background: linear-gradient(to bottom, rgba(253, 66, 29, 0.05) 0%, white 40%);'
);

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: 'This feature is available on 10xTravel+',
    },
    description: {
        type: String,
        default:
            'This feature is part of the 10xTravel+ plan. Upgrade to unlock it and get full access.',
    },
    features: {
        type: Array,
        default: () => [
            'Unlimited credit card benefits tracking',
            'Unlimited bonus points auto-sync',
            'Members-only travel community',
            'Best credit cards to use',
            'Import redemptions from email',
            'Transfer bonus alerts',
        ],
    },
});

const emit = defineEmits(['close']);

// Hide the transfer bonus alerts line when the alerts feature is not enabled.
const visibleFeatures = computed(() =>
    transferBonusAlertsEnabled.value
        ? props.features
        : props.features.filter((feature) => feature !== 'Transfer bonus alerts')
);

const billingCycle = ref('yearly');

const ctaLabel = computed(() => {
    return billingCycle.value === 'yearly'
        ? 'Get 10xTravel+ for $199/year'
        : 'Get 10xTravel+ for $10/month';
});

const handleUpgrade = () => {
    router.visit(
        `/checkout?billing=${billingCycle.value === 'yearly' ? 'year' : 'month'}`
    );
};
</script>
